import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import ReactTooltip from 'react-tooltip';

import { selectFees } from 'store/entities/market/selectors';
import { orderbookSelector } from 'store/entities/market/orderbook/selectors';

import { useLimitPriceValidation } from 'hooks';
import valueWithPrecision from 'helpers/valueWithPrecision';
import vwapCalc from 'helpers/vwapCalc';

import { OrderSide, OrderType, InstrumentT } from 'types';

import './CreateQuote.scss';

import InfoIcon from 'assets/images/info-circle.svg';
interface Props {
  label: string;
  modeMarket?: boolean;
  instrument: InstrumentT | null;
  currency: string;
  amount: string;
  currentPrice: string;
  baseCurrencyAmount: string;
  tradingFee?: string;
  networkFee?: string;
  orderSide: OrderSide;
  orderType: OrderType;

  handlerChangeLimitPrice: Function;
  handlerError?: Function;
}

const CreateQuote: FC<Props> = ({
  label = '',
  modeMarket = false,
  instrument,
  currency = '',
  amount = '',
  currentPrice = '',
  tradingFee = '',
  networkFee = '',
  orderSide = OrderSide.Sell,
  orderType,

  handlerChangeLimitPrice = () => {},
  handlerError = () => {},
}) => {
  const orderbook = useSelector(orderbookSelector);
  const [limitCurrentPrice, setLimitCurrentPrice] = useState(currentPrice);
  const [tradingFeeFormatted, setTradingFeeFormatted] = useState('0');

  const [totalAmount, setTotalAmount] = useState('0');
  const [slippageAmount, setSlippageAmount] = useState('0');
  const [focusInput, setFocusInput] = useState(false);

  let validationError: string = useLimitPriceValidation(limitCurrentPrice, orderType, orderSide, focusInput);

  const fee = useSelector(selectFees);

  const isMarket = orderType === OrderType.Market;
  const isLimit = orderType === OrderType.Limit;

  useEffect(() => {
    if (orderType === OrderType.Market) {
      validationError = '';
      handlerError(false);
    }
  }, [orderType]);

  useEffect(() => {
    setLimitCurrentPrice(currentPrice);
  }, [currentPrice]);

  useEffect(() => {
    if (validationError) {
      handlerError(true);
    } else {
      handlerError(false);
    }
  }, [validationError]);

  useEffect(() => {
    const precision =
      orderSide === OrderSide.Sell ? instrument?.quote_currency.precision : instrument?.base_currency.precision;
    if (!Number(amount) || !Number(limitCurrentPrice)) {
      setSlippageAmount(valueWithPrecision(0, precision));
      setTradingFeeFormatted(valueWithPrecision(0, precision));
      setTotalAmount(valueWithPrecision(0, precision));
      return;
    }

    let calcTradingFee =
      orderSide === OrderSide.Sell
        ? Number(amount) * Number(limitCurrentPrice) * Number(Number(tradingFee) / 100000)
        : (Number(amount) / Number(limitCurrentPrice)) * Number(Number(tradingFee) / 100000);

    let calcTotal =
      orderSide === OrderSide.Sell
        ? Number(amount) * Number(limitCurrentPrice) - calcTradingFee
        : Number(amount) / Number(limitCurrentPrice) - calcTradingFee;

    if (calcTradingFee < 0.0001) {
      calcTradingFee = 0.0;
    }

    if (modeMarket) {
      const vwap = vwapCalc(amount, orderbook, orderSide) || 0;
      let calculatedSlippage = '0';

      if (orderSide === OrderSide.Sell) {
        calcTotal = Number(amount) * vwap - Number(calcTradingFee);
      } else {
        calcTotal = Number(amount) / vwap - Number(calcTradingFee);
      }

      if (!Number(amount)) calcTotal = 0;
      calculatedSlippage = String(Number(calcTotal) - Number(calcTotal) * (Number(fee?.slippage) / 100));
      setSlippageAmount(valueWithPrecision(calculatedSlippage, precision));
    }

    setTradingFeeFormatted(valueWithPrecision(calcTradingFee, precision));
    setTotalAmount(String(calcTotal) >= '0' ? valueWithPrecision(calcTotal, precision) : '0');
  }, [orderSide, amount, limitCurrentPrice]);

  const handlerChange = (e: any) => {
    let inputValue = e.target.value;

    // check if "." is the first input element
    if (inputValue === '.') {
      handlerChangeLimitPrice(String('0.'));
      setLimitCurrentPrice(String('0.'));
      return;
    }

    const onlyLetters = inputValue.match(/[0-9]*\.?[0-9]*/);
    let valueWithPrecision = onlyLetters;

    // Make from 0.1 to value with precision 0.100000
    if (onlyLetters[0].split('.')[1] && onlyLetters[0].split('.')[1].length) {
      if (onlyLetters[0].split('.')[1].length > (instrument?.price_decimals || 0)) {
        let cutValue = onlyLetters[0].split('.')[1].split('').slice(0, instrument?.price_decimals).join('');
        valueWithPrecision = `${onlyLetters[0].split('.')[0]}.${cutValue}`;
      }
    }

    handlerChangeLimitPrice(valueWithPrecision);
    setLimitCurrentPrice(valueWithPrecision);
  };

  const addSymbol = (amount: any, currency: string) => {
    if (currency === 'USDC' && amount <= 0.01) return '<0.01';
    if (amount <= 0.0001) return '<0.0001';
    return `~${amount}`;
  };

  const handlerFocus = () => {
    setFocusInput(true);
  };

  return (
    <div className="create-qoute">
      <div className={`create-qoute-amount-box ${validationError ? 'error' : ''}`}>
        <span className="create-qoute-am">{label}</span>
        <input
          type="text"
          placeholder="0"
          value={modeMarket ? totalAmount : limitCurrentPrice}
          onChange={handlerChange}
          onFocus={handlerFocus}
          disabled={modeMarket}
          className={`create-qoute-amount`}
        />

        {isMarket && (
          <div className="create-qoute-currency">
            <i
              className="create-qoute-currency-icon"
              style={{ backgroundImage: `url(/assets/currency-icons/${currency}.svg)` }}
            />
            <p className="create-qoute-currency-title">{currency}</p>
          </div>
        )}

        {isLimit && instrument && (
          <div className="create-qoute-instrument">
            <p className="instrument-icons">
              <i
                className="create-qoute-instrument-icon left"
                style={{ backgroundImage: `url(/assets/currency-icons/${instrument?.base_currency_id}.svg)` }}
              />
              <i
                className="create-qoute-instrument-icon right"
                style={{ backgroundImage: `url(/assets/currency-icons/${instrument?.quote_currency_id}.svg)` }}
              />
            </p>
            <p className="instrument">{`${instrument?.base_currency_id} / ${instrument?.quote_currency_id}`}</p>
          </div>
        )}
      </div>

      <div className="create-qoute-details">
        {validationError && <p className="create-qoute-details-error">{validationError}</p>}
        <p className="create-qoute-details-acc-balance">
          <span className="create-qoute-details-title">
            {_t('Trading Fee', 'CREATE_ORDER.TRADING_FEE')} ({Number(Number(tradingFee) / 1000)}%)
          </span>
          <span className="create-qoute-details-amount">
            {addSymbol(tradingFeeFormatted, currency)} {currency}
          </span>
        </p>
        <div className="create-qoute-details-min-balance">
          <span className="create-qoute-details-title">
            {_t('Network Fee Deposit', 'CREATE_ORDER.NETWORK_FEE_DEPOSIT')}
            <img
              src={InfoIcon}
              alt="info"
              className="create-qoute-details-min-balance-icon"
              data-tip
              data-for="network-fee-info"
            />
            <ReactTooltip id="network-fee-info" place="right" type="info" effect="solid">
              <span>
                {_t('Each transaction requires a deposit of a network escrow fee.', 'CREATE_ORDER.NETWORK_INFO')}
              </span>
            </ReactTooltip>
          </span>
          <span className="create-qoute-details-amount">
            {networkFee} {_t('AVAX', 'CREATE_ORDER.AVAX')}
          </span>
        </div>
        {!modeMarket && (
          <div className="create-qoute-details-total">
            <span className="create-qoute-details-total-title">{_t('Total', 'CREATE_ORDER.TOTAL')}</span>
            <span className="create-qoute-details-total-amount">
              {totalAmount} {currency}
            </span>
          </div>
        )}
        {/* DO NOT DELETE IT */}
        {/* {modeMarket && (
          <div className="create-qoute-details-max-trade">
            <span className="create-qoute-details-title">
              Min Received After Slippage
              <img
                src={InfoIcon}
                alt="info"
                className="create-qoute-details-min-balance-icon"
                data-tip
                data-for="min-received-after-slippage"
              />
              <ReactTooltip id="min-received-after-slippage" place="right" type="info" effect="solid">
                <span>
                  Slippage is the difference between the expected price of a market order and the price at which the
                  order is executed. Cables set the max slippage for market orders to 5%
                </span>
              </ReactTooltip>
            </span>
            <span className="create-qoute-details-amount">
              {slippageAmount} {currency}
            </span>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default CreateQuote;
